/**
 * Responsibility: warm every row's detail in the background, so opening one
 * is instant, and jump the queue for whatever the operator actually clicks.
 *
 * A card's detail costs a catalogue search and a product read - about eight
 * seconds the first time - and that wait landed on whoever opened the
 * dialog. Fetching them ahead of time moves the wait to where nobody is
 * looking.
 *
 * **One at a time, with a gap.** These are somebody else's free pages, and
 * fifty cards is a hundred-odd requests; firing them together would be rude
 * enough to get the feature blocked, and would not be faster in any way that
 * matters since nothing is waiting on them. The whole board warms in roughly
 * eight minutes, which is fine for work nobody is watching.
 *
 * **A click jumps the queue.** The queue is walked from a list that is
 * re-read on every step rather than captured once, so promoting a key takes
 * effect on the next item instead of after the other forty-nine. Without
 * that, clicking row fifty while the queue is on row two means waiting for
 * both the prefetch and the click to finish the same request.
 *
 * Results land in the same react-query cache the dialog reads, keyed
 * identically, so a warmed card renders with no request at all.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/** Gap between prefetches. Courtesy, not correctness. */
export const QUEUE_GAP_MS = 1500;

type DetailQueueOptions<Key> = {
  /** Everything worth warming, in the order it is shown. */
  keys: Key[];
  /** Cache key for one item, matching whatever the dialog reads. */
  queryKey: (key: Key) => readonly unknown[];
  /** Fetches one item. */
  fetch: (key: Key) => Promise<unknown>;
  /** Off by default on a board nobody has opened. */
  enabled?: boolean;
};

export function useDetailQueue<Key extends string>({
  keys,
  queryKey,
  fetch,
  enabled = true,
}: DetailQueueOptions<Key>) {
  const client = useQueryClient();

  // Read by the walker on every step, so a promotion or a new board takes
  // effect immediately rather than at the end of the current pass.
  const pending = useRef<Key[]>([]);
  const running = useRef(false);
  const stopped = useRef(false);

  /** Move a key to the front, so the next step fetches it. */
  const promote = useCallback((key: Key) => {
    const rest = pending.current.filter((candidate) => candidate !== key);
    pending.current = [key, ...rest];
  }, []);

  useEffect(() => {
    if (!enabled || keys.length === 0) return undefined;

    // Anything already cached is done; re-warming it would spend a request
    // to learn what is already known.
    pending.current = keys.filter((key) => client.getQueryData(queryKey(key)) === undefined);
    stopped.current = false;

    const walk = async () => {
      if (running.current) return;
      running.current = true;

      while (!stopped.current && pending.current.length > 0) {
        const [next, ...rest] = pending.current;
        pending.current = rest;

        if (client.getQueryData(queryKey(next)) === undefined) {
          try {
            await client.prefetchQuery({
              queryKey: queryKey(next),
              queryFn: () => fetch(next),
              staleTime: Infinity,
            });
          } catch {
            // A card that will not load is not worth stopping the queue
            // for; the dialog will surface the failure if it is opened.
          }
          if (stopped.current) break;
          await new Promise((resolve) => setTimeout(resolve, QUEUE_GAP_MS));
        }
      }

      running.current = false;
    };

    void walk();

    return () => {
      // Leaving the page stops the queue. The cache keeps whatever it
      // managed to warm, so coming back resumes rather than restarts.
      stopped.current = true;
    };
  }, [keys, enabled, client, queryKey, fetch]);

  return { promote };
}
